import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Upload,
  Stethoscope,
  Shield,
  FileQuestion
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_camp-cert-tracker/artifacts/2283n1sz_rr%20logo.png";

const certNavItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/staff", label: "Staff", icon: Users },
  { path: "/reports", label: "Reports", icon: FileText },
  { path: "/settings", label: "Settings", icon: Settings },
];

const medicalNavItems = [
  { path: "/medical/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/medical/persons", label: "Persons", icon: Users },
  { path: "/medical/bulk-upload", label: "Bulk Upload", icon: Upload },
  { path: "/medical/unmatched", label: "Unmatched", icon: FileQuestion },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function AppLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine active system based on current path
  const isMedical = location.pathname.startsWith("/medical");
  const isMedicalOnly = user?.role === "medical";
  const showToggle = !isMedicalOnly; // Only show toggle for admin users who can see both

  // Medical-only users always see medical nav items
  const navItems = (isMedicalOnly || isMedical) ? medicalNavItems : certNavItems;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSystem = () => {
    if (isMedical) {
      navigate("/dashboard");
    } else {
      navigate("/medical/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo + System Toggle */}
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Rolling River Day Camp" 
                className="h-10 w-10 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-brand-navy tracking-tight">
                  {isMedicalOnly ? "Medical Forms" : isMedical ? "Medical Forms" : "Staff Cert Tracker"}
                </h1>
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  Rolling River Day Camp
                </p>
              </div>

              {/* System Toggle */}
              {showToggle && (
              <div className="hidden sm:flex ml-4 bg-slate-100 rounded-lg p-0.5" data-testid="system-toggle">
                <button
                  onClick={() => !isMedical ? null : navigate("/dashboard")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    !isMedical
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="toggle-certs"
                >
                  <Shield className="h-3.5 w-3.5" />
                  Certs
                </button>
                <button
                  onClick={() => isMedical ? null : navigate("/medical/dashboard")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    isMedical
                      ? "bg-teal-700 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="toggle-medical"
                >
                  <Stethoscope className="h-3.5 w-3.5" />
                  Medical
                </button>
              </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? isMedical
                          ? "bg-teal-700 text-white"
                          : "bg-brand-navy text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-4 w-4" strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* User Actions */}
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-slate-500">
                {user?.username}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                <span className="hidden sm:inline">Logout</span>
              </Button>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            {/* Mobile System Toggle */}
            {showToggle && (
            <div className="px-4 pt-3 pb-2 flex gap-2">
              <button
                onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold ${
                  !isMedical
                    ? "bg-brand-navy text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
                data-testid="mobile-toggle-certs"
              >
                <Shield className="h-3.5 w-3.5" />
                Certs
              </button>
              <button
                onClick={() => { navigate("/medical/dashboard"); setMobileMenuOpen(false); }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold ${
                  isMedical
                    ? "bg-teal-700 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
                data-testid="mobile-toggle-medical"
              >
                <Stethoscope className="h-3.5 w-3.5" />
                Medical
              </button>
            </div>
            )}
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium ${
                      isActive
                        ? isMedical
                          ? "bg-teal-700 text-white"
                          : "bg-brand-navy text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
